import React from "react";

import { Icon, IconSize } from "../../Atoms/Icons/Icon/Icon";

import styles from "./LinkButton.module.scss";

type LinkButtonSize = "xs" | "sm" | "md" | "lg";
type LinkButtonVariant = "brand" | "warning" | "error" | "success" | "neutral";

interface ILinkButtonProps {
    /**
     * The text displayed on the button.
     */
    label: string;

    /**
     * A callback to invoke when the button is clicked.
     */
    onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

    /**
     * The size of the button.
     * The default value is md.
     */
    size?: LinkButtonSize;

    /**
     * The style variant of the button.
     * The default value is brand.
     */
    variant?: LinkButtonVariant;

    /**
     * A data-cy attribute that will be applied to the button to be able to target it in integration tests.
     */
    dataCy?: string;

    /**
     * Determines if the underline is shown.
     * The default value is true.
     */
    underline?: boolean;

    /**
     * Whether the button is disabled.
     */
    disabled?: boolean;

    /**
     * The icon component that will be rendered on the left or right of the label in the link button.
     */
    children?: JSX.Element;

    /**
     * The position of the icon relative to the link text.
     * This value is used only when the children is provided.
     * Default value is "left".
     */
    iconPosition?: "left" | "right";
}

// lg/md buttons use smaller icons sizes to avoid visual imbalance
const iconSizeMapper: Record<LinkButtonSize, IconSize> = {
    lg: "md",
    md: "sm",
    sm: "sm",
    xs: "xs"
} as const;

/**
 * The link button component.
 * @param {ILinkButtonProps} param The props used to render the link button.
 * @returns The rendered component.
 */
function LinkButton({
    label,
    onClick,
    size = "md",
    variant = "brand",
    dataCy,
    underline = true,
    disabled = false,
    children,
    iconPosition = "left"
}: Readonly<ILinkButtonProps>): JSX.Element {
    const sizeClass = styles[size];
    const variantClass = styles[variant];
    const underlineClass = underline ? styles.underline : "";

    return (
        <button
            onClick={onClick}
            className={`${styles.linkButton} ${sizeClass} ${variantClass} ${underlineClass}`}
            data-cy={dataCy}
            disabled={disabled}
            type="button"
        >
            <span className={`${styles.label} ${(!children || iconPosition === "left") ? "" : styles.right}`}>
                {children && (
                    <span className={styles.icon}>
                        <Icon size={iconSizeMapper[size]}>
                            {children}
                        </Icon>
                    </span>
                )}
                <span>{label}</span>
            </span>
        </button>
    );
}

export { ILinkButtonProps, LinkButton, LinkButtonSize, LinkButtonVariant };
